import { stringify } from 'qs';
import { format } from 'date-fns';

import { useBookingStore } from '~/domains/booking';
import { useUserStore } from '~/domains/user';

export interface RoomConfig {
  adults: number;
  childrenAges?: number[];
}

export interface ExternalSearchQuery {
  destination?: string;
  dates?: Date[];
  roomsAndGuests?: RoomConfig[];
  isLogged?: boolean;
  tags?: [string];
}

export const useExternalNavigation = () => {
  const bookingStore = useBookingStore();
  const { locale } = useI18n();

  const buildUrl = (
    baseUrl: string,
    path: string,
    queryParams?: Record<string, any>,
  ) => {
    const queryString = queryParams
      ? stringify(queryParams, {
          arrayFormat: 'indices',
          allowDots: true,
          skipNulls: true,
          sort: (a, b) => a.localeCompare(b),
          filter: (_prefix: string, value: unknown) => {
            if (value === '' || value === undefined) return;
            return value;
          },
        })
      : '';

    const cleanPath = path.replace(/^\/+/, '');

    // eslint-disable-next-line sonarjs/no-nested-template-literals
    return `${baseUrl}/${locale.value}/${cleanPath}${queryString ? `?${queryString}` : ''}`;
  };

  const getRedirectToSearchUrl = (
    baseUrl: string,
    params?: ExternalSearchQuery,
  ) => {
    const redirectParams: Record<string, any> = {};

    if (params && 'isLogged' in params) {
      redirectParams.login = params.isLogged;
    }

    if (params?.destination) {
      redirectParams.filter = { countries: [params.destination] };
    }

    if (params?.tags?.length) {
      redirectParams.filter = {
        ...redirectParams.filter,
        tags: params.tags,
      };
    }

    if (params?.dates && params.dates.length >= 2) {
      redirectParams.checkin = format(params.dates[0], 'yyyy-MM-dd');
      redirectParams.checkout = format(params.dates[1], 'yyyy-MM-dd');
    }

    if (bookingStore.currentCurrency) {
      redirectParams.currency = bookingStore.currentCurrency;
    }

    if (params?.roomsAndGuests && params.roomsAndGuests.length > 0) {
      redirectParams.rooms = params.roomsAndGuests.map(room => {
        const hasChildren = room.childrenAges && room.childrenAges.length > 0;

        return {
          adults: room.adults,
          ...(hasChildren && {
            children: room.childrenAges!.length,
            childrenAges: room.childrenAges,
          }),
        };
      });
    } else {
      redirectParams.rooms = [
        {
          adults: 2,
        },
      ];
    }

    return buildUrl(baseUrl, 'search', redirectParams);
  };

  const redirectToSearch = async (
    baseUrl: string,
    params?: ExternalSearchQuery,
  ) => {
    const url = getRedirectToSearchUrl(baseUrl, params);
    await navigateTo(url, { external: true });
  };

  return {
    redirectToSearch,
    buildUrl,
    getRedirectToSearchUrl,
  };
};
